import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, Car, Euro, Info, TrendingUp, Fuel, Wrench } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';

// Standard mileage rates in the Netherlands (2024/2025)
const RATES = {
  belastingvrij: 0.23,    // Belastingvrije vergoeding per km
  zakelijk_gemiddeld: 0.25, // Gemiddeld zakelijk tarief
  lease_elektrisch: 0.22,  // Elektrisch lease tarief
  lease_brandstof: 0.28,   // Brandstof lease tarief
  eigen_auto_zakelijk: 0.35 // Eigen auto zakelijk gebruik
};

// Car cost estimates per km by type
const CAR_COSTS = {
  klein: { brandstof: 0.08, onderhoud: 0.03, afschrijving: 0.10, verzekering: 0.02 },
  midden: { brandstof: 0.10, onderhoud: 0.04, afschrijving: 0.15, verzekering: 0.03 },
  groot: { brandstof: 0.14, onderhoud: 0.05, afschrijving: 0.20, verzekering: 0.04 },
  elektrisch: { energie: 0.04, onderhoud: 0.02, afschrijving: 0.18, verzekering: 0.03 }
};

export default function KilometerCalculatorPage() {
  const { t } = useTranslation();
  const [kilometers, setKilometers] = useState('');
  const [tarief, setTarief] = useState('0.23');
  const [autoType, setAutoType] = useState('midden');
  const [periodeType, setPeriodeType] = useState('maand');
  const [werkdagen, setWerkdagen] = useState('22');
  const [results, setResults] = useState(null);

  const calculateResults = () => {
    const km = parseFloat(kilometers) || 0;
    const rate = parseFloat(tarief) || 0.23;
    const days = parseInt(werkdagen) || 22;
    
    // Calculate period multipliers
    let periodMultiplier = 1;
    let periodLabel = 'per maand';
    if (periodeType === 'dag') {
      periodMultiplier = 1;
      periodLabel = 'per dag';
    } else if (periodeType === 'week') {
      periodMultiplier = 5; // 5 werkdagen
      periodLabel = 'per week';
    } else if (periodeType === 'maand') {
      periodMultiplier = days;
      periodLabel = 'per maand';
    } else if (periodeType === 'jaar') {
      periodMultiplier = days * 12;
      periodLabel = 'per jaar';
    }
    
    const totalKm = km * periodMultiplier;
    const vergoeding = totalKm * rate;
    const belastingvrijMax = totalKm * RATES.belastingvrij;
    
    // Calculate actual car costs
    const costs = CAR_COSTS[autoType] || CAR_COSTS.midden;
    const actualCosts = {
      brandstof: totalKm * (costs.brandstof || costs.energie || 0),
      onderhoud: totalKm * costs.onderhoud,
      afschrijving: totalKm * costs.afschrijving,
      verzekering: totalKm * costs.verzekering
    };
    const totalCosts = Object.values(actualCosts).reduce((a, b) => a + b, 0);
    
    // Net benefit/loss
    const netResult = vergoeding - totalCosts;
    
    setResults({
      totalKm,
      vergoeding,
      belastingvrijMax,
      bovenBelastingvrij: vergoeding > belastingvrijMax ? vergoeding - belastingvrijMax : 0,
      actualCosts,
      totalCosts,
      netResult,
      periodLabel,
      costPerKm: totalKm > 0 ? totalCosts / totalKm : 0
    });
  };

  return (
    <div className="space-y-6" data-testid="kilometer-calculator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('kmCalculator.title')}</h1>
        <p className="text-muted-foreground">{t('kmCalculator.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Calculator className="w-5 h-5 text-cyan-500" />
              {t('kmCalculator.calculateReimbursement')}
            </CardTitle>
            <CardDescription>
              {t('kmCalculator.fillInDetails')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-foreground">{t('kmCalculator.kilometersSingleTrip')}</Label>
              <Input
                type="number"
                value={kilometers}
                onChange={(e) => setKilometers(e.target.value)}
                placeholder={t('kmCalculator.exampleKm')}
                className="bg-background"
                data-testid="km-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('kmCalculator.commuteOrBusiness')}
              </p>
            </div>

            <div>
              <Label className="text-foreground">{t('kmCalculator.period')}</Label>
              <Select value={periodeType} onValueChange={setPeriodeType}>
                <SelectTrigger className="bg-background" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dag">{t('kmCalculator.perDay')}</SelectItem>
                  <SelectItem value="week">{t('kmCalculator.perWeek')}</SelectItem>
                  <SelectItem value="maand">{t('kmCalculator.perMonth')}</SelectItem>
                  <SelectItem value="jaar">{t('kmCalculator.perYear')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(periodeType === 'maand' || periodeType === 'jaar') && (
              <div>
                <Label className="text-foreground">{t('kmCalculator.workdaysPerMonth')}</Label>
                <Input
                  type="number"
                  value={werkdagen}
                  onChange={(e) => setWerkdagen(e.target.value)}
                  placeholder="22"
                  className="bg-background"
                  data-testid="workdays-input"
                />
              </div>
            )}

            <div>
              <Label className="text-foreground">{t('kmCalculator.mileageRate')}</Label>
              <Select value={tarief} onValueChange={setTarief}>
                <SelectTrigger className="bg-background" data-testid="rate-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.23">{t('kmCalculator.rate023')}</SelectItem>
                  <SelectItem value="0.25">{t('kmCalculator.rate025')}</SelectItem>
                  <SelectItem value="0.30">{t('kmCalculator.rate030')}</SelectItem>
                  <SelectItem value="0.35">{t('kmCalculator.rate035')}</SelectItem>
                  <SelectItem value="custom">{t('kmCalculator.customRate')}</SelectItem>
                </SelectContent>
              </Select>
              {tarief === 'custom' && (
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.23"
                  onChange={(e) => setTarief(e.target.value)}
                  className="bg-background mt-2"
                />
              )}
            </div>

            <div>
              <Label className="text-foreground">{t('kmCalculator.carType')}</Label>
              <Select value={autoType} onValueChange={setAutoType}>
                <SelectTrigger className="bg-background" data-testid="car-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="klein">{t('kmCalculator.carSmall')}</SelectItem>
                  <SelectItem value="midden">{t('kmCalculator.carMedium')}</SelectItem>
                  <SelectItem value="groot">{t('kmCalculator.carLarge')}</SelectItem>
                  <SelectItem value="elektrisch">{t('kmCalculator.carElectric')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={calculateResults} 
              className="w-full"
              data-testid="calculate-btn"
            >
              <Calculator className="w-4 h-4 mr-2" />
              {t('kmCalculator.calculate')}
            </Button>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Euro className="w-5 h-5 text-green-500" />
              {t('kmCalculator.results')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {results ? (
              <div className="space-y-6">
                {/* Main Results */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">{t('kmCalculator.totalKilometers')}</p>
                    <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                      {formatNumber(results.totalKm)} km
                    </p>
                    <p className="text-xs text-muted-foreground">{results.periodLabel}</p>
                  </div>
                  <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                    <p className="text-sm text-muted-foreground">{t('kmCalculator.reimbursement')}</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(results.vergoeding)}
                    </p>
                    <p className="text-xs text-muted-foreground">{results.periodLabel}</p>
                  </div>
                </div>

                {/* Tax Info */}
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('kmCalculator.taxFreeReimbursement')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('kmCalculator.maxTaxFree')}: {formatCurrency(results.belastingvrijMax)} ({formatCurrency(RATES.belastingvrij)}/km)
                      </p>
                      {results.bovenBelastingvrij > 0 && (
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                          {t('kmCalculator.aboveTaxFree', { amount: formatCurrency(results.bovenBelastingvrij) })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div>
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    {t('kmCalculator.actualCarCosts')}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Fuel className="w-4 h-4" />
                        {t('kmCalculator.fuelEnergy')}
                      </span>
                      <span className="text-foreground">{formatCurrency(results.actualCosts.brandstof)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('kmCalculator.maintenance')}</span>
                      <span className="text-foreground">{formatCurrency(results.actualCosts.onderhoud)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('kmCalculator.depreciation')}</span>
                      <span className="text-foreground">{formatCurrency(results.actualCosts.afschrijving)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('kmCalculator.insurance')}</span>
                      <span className="text-foreground">{formatCurrency(results.actualCosts.verzekering)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium pt-2 border-t border-border">
                      <span className="text-foreground">{t('kmCalculator.totalCosts')}</span>
                      <span className="text-foreground">{formatCurrency(results.totalCosts)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('kmCalculator.costPerKm')}</span>
                      <span className="text-foreground">{formatCurrency(results.costPerKm)}/km</span>
                    </div>
                  </div>
                </div>

                {/* Net Result */}
                <div className={`p-4 rounded-lg ${results.netResult >= 0 ? 'bg-green-50 dark:bg-green-950/30' : 'bg-red-50 dark:bg-red-950/30'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`w-5 h-5 ${results.netResult >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                      <span className="font-medium text-foreground">{t('kmCalculator.netResult')}</span>
                    </div>
                    <span className={`text-xl font-bold ${results.netResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {results.netResult >= 0 ? '+' : ''}{formatCurrency(results.netResult)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {results.netResult >= 0 
                      ? t('kmCalculator.netResultPositive')
                      : t('kmCalculator.netResultNegative')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('kmCalculator.fillAndCalculate')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground text-lg">{t('kmCalculator.taxRulesTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• <strong className="text-foreground">€ 0,23/km</strong> {t('kmCalculator.taxRule1')}</p>
            <p>• {t('kmCalculator.taxRule2')}</p>
            <p>• {t('kmCalculator.taxRule3')}</p>
            <p>• {t('kmCalculator.taxRule4')}</p>
          </CardContent>
        </Card>
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-foreground text-lg">{t('kmCalculator.tipsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• {t('kmCalculator.tip1')}</p>
            <p>• {t('kmCalculator.tip2')}</p>
            <p>• {t('kmCalculator.tip3')}</p>
            <p>• {t('kmCalculator.tip4')}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
